/**
 * Course settings · "Evaluare inițială" (15.10.2026): the one switch for the
 * questionnaire's automatic emails. It is off until the owner turns it on;
 * even on, a group sends nothing until somebody chooses how it gets it, in
 * the portal's opening dialog or on the group's sheet.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { SWITCH_HINT, SWITCH_TITLE } from "../../../lib/initialAssessmentAdmin";

export default function AssessmentSettings({ canMutate }) {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/initial-assessment/settings");
      setEnabled(Boolean(r.data?.enabled));
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!canMutate || busy) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/initial-assessment/settings", { enabled: !enabled });
      setEnabled(Boolean(r.data?.enabled));
      toast.success(r.data?.enabled ? "Evaluarea inițială pleacă automat." : "Evaluarea inițială nu mai pleacă automat.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva comutatorul."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="settings-tab-evaluare">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-assessment">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
          <ClipboardList size={16} /> Evaluare inițială
        </div>
        {failed ? (
          <p className="text-sm text-[#6B7280]">Nu am putut încărca setarea.</p>
        ) : enabled === null ? (
          <p className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="animate-spin" size={16} /> Se încarcă…</p>
        ) : (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-[#965EA7]/20 bg-[#F0E7F4]/40 p-4">
            <div className="min-w-0">
              <div className="font-semibold text-[#373A45]">{SWITCH_TITLE}</div>
              <div className="mt-1 max-w-2xl text-xs text-[#6B7280]">{SWITCH_HINT}</div>
              <div className="mt-2 max-w-2xl text-xs text-[#6B7280]">
                Pentru fiecare grupă alegi separat dacă primește chestionarul automat, îl trimiți tu sau l-a completat
                în altă parte: la deschiderea portalului sau din fișa grupei. Până alegi, grupa nu primește nimic.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={SWITCH_TITLE}
              disabled={!canMutate || busy}
              onClick={toggle}
              className="shrink-0 text-[#965EA7] disabled:opacity-40"
              data-testid="settings-assessment-toggle"
            >
              {enabled ? <ToggleRight size={40} /> : <ToggleLeft size={40} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
